import React from "react";
import { AbsoluteFill, Easing, interpolate, useCurrentFrame } from "remotion";

const ACCENT = "#5B8CFF";

const FONT =
  '"SF Pro Display", -apple-system, BlinkMacSystemFont, "Helvetica Neue", Inter, "Noto Sans CJK SC", "Source Han Sans SC", "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", sans-serif';

const EASE_OUT_EXPO = Easing.bezier(0.16, 1, 0.3, 1);
const EASE_SOFT = Easing.bezier(0.33, 1, 0.68, 1);

const CLAMP = {
  extrapolateLeft: "clamp" as const,
  extrapolateRight: "clamp" as const,
};

const ITEMS = [
  { word: "CREATE", desc: "Turn ideas into experiences." },
  { word: "UNDERSTAND", desc: "See the context behind the request." },
  { word: "EXECUTE", desc: "Turn decisions into results." },
];

const PAD_X = 190;
const LIST_TOP = 300;
const ROW_H = 168;
const SPOT_START = 78;
const SPOT_STEP = 54;

export const Capabilities: React.FC = () => {
  const frame = useCurrentFrame();

  // ----------------------------------------------------------------- camera
  const cameraY = interpolate(frame, [0, 240], [10, -10], {
    ...CLAMP,
    easing: Easing.linear,
  });

  const bgIn = interpolate(frame, [0, 50], [0, 1], {
    ...CLAMP,
    easing: EASE_SOFT,
  });

  // ----------------------------------------------------------------- header
  const headReveal = interpolate(frame, [4, 40], [0, 1], {
    ...CLAMP,
    easing: EASE_OUT_EXPO,
  });
  const headOpacity = interpolate(frame, [4, 34], [0, 1], {
    ...CLAMP,
    easing: EASE_SOFT,
  });
  const eyebrowOpacity = interpolate(frame, [0, 26], [0, 1], {
    ...CLAMP,
    easing: EASE_SOFT,
  });

  // ------------------------------------------- spotlight travelling the list
  // Each row lights up as the spotlight reaches it, then releases as the
  // spotlight moves on. The last row keeps the light.
  const spotlight = (i: number) => {
    const inStart = SPOT_START + i * SPOT_STEP;
    const outStart = SPOT_START + (i + 1) * SPOT_STEP;
    const rise = interpolate(frame, [inStart, inStart + 20], [0, 1], {
      ...CLAMP,
      easing: EASE_SOFT,
    });
    const fall = interpolate(frame, [outStart, outStart + 20], [1, 0], {
      ...CLAMP,
      easing: EASE_SOFT,
    });
    return Math.min(rise, fall);
  };

  // Continuous position so the background light glides rather than jumps.
  const spotIndex = interpolate(
    frame,
    [SPOT_START, SPOT_START + SPOT_STEP, SPOT_START + SPOT_STEP * 2],
    [0, 1, 2],
    { ...CLAMP, easing: EASE_SOFT },
  );
  const glowY = LIST_TOP + spotIndex * ROW_H + ROW_H / 2;

  // ------------------------------------------------------------------ exit
  const exit = interpolate(frame, [226, 240], [1, 0], {
    ...CLAMP,
    easing: EASE_SOFT,
  });

  return (
    <AbsoluteFill
      style={{
        backgroundColor: "#050507",
        fontFamily: FONT,
        opacity: exit,
      }}
    >
      {/* light that follows the active row */}
      <AbsoluteFill style={{ opacity: bgIn }}>
        <div
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            top: glowY - 320,
            height: 640,
            background:
              "radial-gradient(46% 50% at 34% 50%, rgba(91,140,255,0.17) 0%, rgba(91,140,255,0.05) 46%, rgba(0,0,0,0) 74%)",
          }}
        />
      </AbsoluteFill>

      {/* geometry: continuation of the hero's ring language, off-canvas right */}
      <AbsoluteFill style={{ opacity: bgIn * 0.9 }}>
        <svg
          width="1920"
          height="1080"
          viewBox="0 0 1920 1080"
          style={{ position: "absolute", top: 0, left: 0 }}
        >
          <g transform="translate(1790 540) rotate(-14)">
            <circle
              r={520}
              fill="none"
              stroke={ACCENT}
              strokeWidth={1}
              strokeOpacity={0.1}
            />
            <circle
              r={360}
              fill="none"
              stroke={ACCENT}
              strokeWidth={1}
              strokeOpacity={0.06}
            />
          </g>
        </svg>
      </AbsoluteFill>

      {/* content */}
      <AbsoluteFill
        style={{ transform: `translateY(${cameraY}px)` }}
      >
        {/* eyebrow */}
        <div
          style={{
            position: "absolute",
            left: PAD_X,
            top: 132,
            display: "flex",
            alignItems: "center",
            gap: 12,
            opacity: eyebrowOpacity,
          }}
        >
          <div
            style={{ width: 22, height: 1, backgroundColor: ACCENT, opacity: 0.85 }}
          />
          <span
            style={{
              fontSize: 12,
              letterSpacing: "0.34em",
              textTransform: "uppercase",
              color: "rgba(255,255,255,0.42)",
              fontWeight: 500,
            }}
          >
            Capabilities
          </span>
        </div>

        {/* heading */}
        <div
          style={{
            position: "absolute",
            left: PAD_X,
            top: 166,
            overflow: "hidden",
            paddingBottom: 12,
            opacity: headOpacity,
          }}
        >
          <div
            style={{
              transform: `translateY(${(1 - headReveal) * 118}%)`,
              fontSize: 38,
              fontWeight: 500,
              letterSpacing: "-0.01em",
              lineHeight: 1.15,
              color: "rgba(255,255,255,0.92)",
            }}
          >
            From idea to execution.
          </div>
        </div>

        {/* hairlines */}
        {[0, 1, 2, 3].map((i) => {
          const lineIn = interpolate(
            frame,
            [18 + i * 18, 18 + i * 18 + 50],
            [0, 1],
            { ...CLAMP, easing: EASE_OUT_EXPO },
          );
          return (
            <div
              key={`line-${i}`}
              style={{
                position: "absolute",
                left: PAD_X,
                right: PAD_X,
                top: LIST_TOP + i * ROW_H,
                height: 1,
                backgroundColor: "rgba(255,255,255,0.08)",
                transform: `scaleX(${lineIn})`,
                transformOrigin: "left center",
              }}
            />
          );
        })}

        {/* rows */}
        {ITEMS.map((item, i) => {
          const reveal = interpolate(
            frame,
            [26 + i * 18, 26 + i * 18 + 44],
            [0, 1],
            { ...CLAMP, easing: EASE_OUT_EXPO },
          );
          const descReveal = interpolate(
            frame,
            [56 + i * 18, 56 + i * 18 + 36],
            [0, 1],
            { ...CLAMP, easing: EASE_OUT_EXPO },
          );
          const spot = spotlight(i);

          return (
            <div
              key={item.word}
              style={{
                position: "absolute",
                left: PAD_X,
                right: PAD_X,
                top: LIST_TOP + i * ROW_H,
                height: ROW_H,
                display: "flex",
                alignItems: "center",
                transform: `translateX(${spot * 12}px)`,
              }}
            >
              <div
                style={{
                  width: 2,
                  height: 46,
                  borderRadius: 1,
                  backgroundColor: ACCENT,
                  marginRight: 36,
                  transform: `scaleY(${spot})`,
                  opacity: spot,
                }}
              />
              <div style={{ display: "flex", flexDirection: "column" }}>
                <div style={{ overflow: "hidden", paddingBottom: 8 }}>
                  <div
                    style={{
                      transform: `translateY(${(1 - reveal) * 118}%)`,
                      fontSize: 62,
                      fontWeight: 600,
                      letterSpacing: "0.05em",
                      lineHeight: 1.05,
                      color: `rgba(255,255,255,${0.26 + spot * 0.74})`,
                    }}
                  >
                    {item.word}
                  </div>
                </div>
                <div
                  style={{
                    marginTop: 12,
                    fontSize: 19,
                    letterSpacing: "0.01em",
                    color: `rgba(255,255,255,${0.34 + spot * 0.34})`,
                    opacity: descReveal,
                    transform: `translateY(${(1 - descReveal) * 10}px)`,
                  }}
                >
                  {item.desc}
                </div>
              </div>
            </div>
          );
        })}
      </AbsoluteFill>

      {/* vignette */}
      <AbsoluteFill
        style={{
          background:
            "radial-gradient(120% 100% at 50% 50%, rgba(0,0,0,0) 45%, rgba(0,0,0,0.5) 100%)",
          pointerEvents: "none",
        }}
      />
    </AbsoluteFill>
  );
};